import { Component, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Batch, Container, LocatedStock, Product, ProductBatchState, TId, WmsLocation } from 'src/app/models';
import { MemoryReadOnlyResource } from 'src/app/resources';
import { MasterApiService } from 'src/app/services/master-api.service';
import { ProductsStorageService } from '../../product-storage.service';
import { DxDataGridComponent } from 'devextreme-angular';
import { WmsContainerEditComponent } from 'src/app/routes/wms/wms-operative/wms-locations/wms-container-edit/wms-container-edit.component';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { GenericCrudEditComponent } from 'src/app/libs/components/generic-crud/generic-crud-edit.component';
import { ProductsBatchEditComponent } from '../../../products-batch/products-batch-edit/products-batch-edit.component';
import { WmsLocationsEditComponent } from 'src/app/routes/wms/wms-operative/wms-locations/wms-locations-edit/wms-locations-edit.component';
import { formatGridNumber } from 'src/app/libs/utils';

@Component({
    selector: 'app-product-located-stock',
    templateUrl: './product-located-stock.component.html',
    styleUrl: './product-located-stock.component.sass',
    standalone: false
})
export class ProductLocatedStockComponent implements OnInit{
  locatedStockResource?: MemoryReadOnlyResource<LocatedStock>
  totalLocatedStock: number = 0;
  recordCount: number = 0;
  formatCode: string = '';
  product?: Product;
  /** Batch states indexed by id, to colour the state dot of each row without a request per row. */
  private batchStates = new Map<TId, ProductBatchState>();

  /** Locale-aware total, so decimals match the grid columns (e.g. "3.232,56"). */
  get totalDisplay(): string {
    return formatGridNumber(this.totalLocatedStock);
  }
  @ViewChild ('grid') dataGrid?: DxDataGridComponent;
  containerEditor = WmsContainerEditComponent;
  batchEditor = ProductsBatchEditComponent;
  locationEditor = WmsLocationsEditComponent;

  constructor(
     private api: MasterApiService,
     private productData: ProductsStorageService,
     private tabs: TabsService
  ){}
  async ngOnInit(): Promise<void> {
    this.product = this.productData.entity;
    const locatedStock = await firstValueFrom(this.api.resources.wms.locatedStock.getAll({ productId: this.product?.id }))
    this.locatedStockResource = new MemoryReadOnlyResource('locatedStock', locatedStock);
    this.updateSummary(locatedStock);
    await this.loadBatchStates();
  }

  private async loadBatchStates(): Promise<void> {
    const companyId = this.api.auth.userValue?.lastCompanyId ?? '';
    const states = await firstValueFrom(this.api.resources.product.productBatchStates.getAll({ companyId }));
    this.batchStates = new Map(states.map(state => [state.id, state]));
  }

  batchStateColor(productBatchStateId?: TId): string | undefined {
    return productBatchStateId === undefined ? undefined : this.batchStates.get(productBatchStateId)?.color;
  }

  batchStateName(productBatchStateId?: TId): string {
    return productBatchStateId === undefined ? '' : this.batchStates.get(productBatchStateId)?.name ?? '';
  }

  /** Recompute the footer summary (record count, total quantity, unit). */
  private updateSummary(locatedStock: LocatedStock[]): void {
    this.recordCount = locatedStock.length;
    this.totalLocatedStock = locatedStock.reduce((sum, stock) => sum + stock.quantity, 0);
    // Show the unit only when every row shares it, so the total isn't mislabelled.
    const codes = [...new Set(locatedStock.map(stock => stock.formatCode).filter(Boolean))];
    this.formatCode = codes.length === 1 ? codes[0] : '';
  }

  async refresh() {
    console.log('estic-refrescant')
    const locatedStock = await firstValueFrom(this.api.resources.wms.locatedStock.getAll({ productId: this.product?.id }));

    // call reset data dor refresh resource data
    this.locatedStockResource?.resetData(locatedStock)

    // update summary and refresh data grid
    this.updateSummary(locatedStock);
    await this.dataGrid?.instance.refresh();
  }

  async navigateToContainerEditor(containerId: TId) {
    const container = await firstValueFrom(this.api.resources.wms.containers.get(containerId));
    const tab = this.tabs.createTab<GenericCrudEditComponent<Container>>(this.containerEditor!, TabRole.EDITION, {inputPrms: {entity: container,   genericInfoAlternativeText: 'entity.container.containerId',}});
  }

  async navigateToBatchEditor(goodId:TId) {
    const batches = await firstValueFrom(this.api.resources.product.productBatches.get(goodId));
    this.tabs.createTab<GenericCrudEditComponent<Batch>>(this.batchEditor!, TabRole.EDITION, { inputPrms: {entity: batches} });
  }

  async navigateToLocationEditor(locationId:TId) {
    const locations = await firstValueFrom(this.api.resources.wms.locations.get(locationId));
    this.tabs.createTab<GenericCrudEditComponent<WmsLocation>>(this.locationEditor!, TabRole.EDITION, { inputPrms: {entity: locations} });
  }
}
